/* eslint-disable camelcase */
import { defineStore } from 'pinia';
import CryptoJS from 'crypto-js';
import type { OAuthState, PostKeys, PostSignUp } from '@/models/login';
import { DeepLinkService, EntryService, LoginService, StudyService } from '@/services';
import { routeConstants, LocalStorageUtils, DEEPLINK_ACTIONS, REDIRECTION_ACTIONS, isAllowedRedirectUrl } from '@/utils';
import { useRoute, useRouter } from 'vue-router';
import { ref } from 'vue';
import { DeepLink, LoadingStateModel } from '@/models';
import studyGroupService from '@/services/studyGroupService';
import type { AxiosError } from 'axios';
import { prepareToast } from '@/models/toast';
import { useAppStore } from './app';
import { useUserStore } from './user';
import { useStudyStore } from './study';
import { useAuthStore } from './auth';

export const useLoginStore = defineStore('login', () => {

  const appStore = useAppStore();
  const userStore = useUserStore();
  const authStore = useAuthStore();
  const studyStore = useStudyStore();

  const STUDYPAGES_CLIENT_ID = import.meta.env.VITE_APP_STUDYPAGES_CLIENT_ID;
  const STUDYPAGES_CODE_VERIFIER = import.meta.env.VITE_APP_STUDYPAGES_CODE_VERIFIER;
  const FRONTEND_BASE_URL = import.meta.env.VITE_APP_FRONTEND_BASE_URL;
  const STUDYPAGES_BASE_URL = import.meta.env.VITE_APP_STUDYPAGES_BASE_URL;
  const ENVIRONMENT = import.meta.env.VITE_APP_ENVIRONMENT;
  const WEB_FRONTEND = 1;
  const WEB_FRONTEND_LOCAL = 3;

  const linkError = ref<string | undefined>();
  const signupError = ref<string | undefined>();

  const studypagesOauth = async (state?: string) => {
    if (authStore.userLoggedIn) {
      if (state) {
        try {
          await studyStore.getStudy(JSON.parse(state).studyIdentifier);
        } catch (error) {
          // study selection might fail
        }
      }
      window.location.href = FRONTEND_BASE_URL;
      return;
    }
    const codeChallenge = CryptoJS.SHA256(STUDYPAGES_CODE_VERIFIER)
      .toString(CryptoJS.enc.Base64)
      .replace(/=/g, '');

    const params = {
      client_id: STUDYPAGES_CLIENT_ID,
      state: state ? state : 'random_state',
      response_type: 'code',
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      redirect_uri: `${FRONTEND_BASE_URL}/studypages/success`
    };

    const oAuthUrl = new URL(`${STUDYPAGES_BASE_URL}/oauth/authorize`);

    //@ts-expect-error just a type issue
    Object.keys(params).forEach(key => oAuthUrl.searchParams.append(key, params[key]));

    if (deepLinkData.value?.email) {
      window.location.href = `${oAuthUrl.toString()}&email=${deepLinkData.value?.email}`;
    } else {
      window.location.href = oAuthUrl.toString();
    }
  };


  const route = useRoute();
  const router = useRouter();

  const handleOAuthRedirections = async () => {
    if (route.query.code && route.fullPath.startsWith('/studypages')) {

      const stateString = route.query.state;
      let stateObject;
      try {
        stateObject = JSON.parse(stateString as string) as OAuthState;
        if (stateObject != undefined) {
          let appUrl: string | undefined;
          switch (stateObject.action) {
            case REDIRECTION_ACTIONS.ACTION_MOBILE_LOGIN_ANDROID:
              appUrl = `app://open.teamscope.app?code=${route.query.code}`;
              break;

            case REDIRECTION_ACTIONS.ACTION_MOBILE_LOGIN_SPDATA:
              appUrl = `teamscope://oauth-success?code=${route.query.code}&state=${route.query.state}`;
              break;

            case REDIRECTION_ACTIONS.ACTION_MOBILE_LOGIN_PUMP:
              appUrl = `pump-surgeon://oauth-success?code=${route.query.code}&state=${route.query.state}`;
              break;
          
            default:
              break;
          }

          if (appUrl) {
            window.location.href = appUrl;
            return;
          }
        }
      } catch (error) {
        // nothing goes here
      }

      /**
       * Updated logic to handle invalid_grant error from OAuth.
       * Using localStorage to maintain loginAttempt state since the OAuth flow recreates the local app state.
       */
      let keys;
      try {
        if (localStorage.getItem('loginAttempt') != '1') {
          localStorage.setItem('loginAttempt', '1');
          keys = await oauthLogin(route.query.code as string);
          localStorage.setItem('loginAttempt', '0');
        } else {
          appStore.addToast(prepareToast('error', 'Login failed', 'Please try again!'));
          localStorage.setItem('loginAttempt', '0');
          router.replace({
            name: routeConstants.LOGIN.name,
            query: {
              'after_logout': 'true'
            }
          });
          return;
        }
      } catch (error) {
        if ((error as AxiosError).status == 400) {
          appStore.addToast(prepareToast('error', 'Login failed', 'Retrying one more time'));
          setTimeout(() => {
            // automatic retry
            studypagesOauth(`${stateString}`);
          }, 1000);
        }
        return;
      }

      authStore.checkAuth();

      if (stateString && typeof stateString === 'string' && isAllowedRedirectUrl(stateString)) {
        // handling the deep links
        window.location.href = stateString;
      } else if (stateObject != undefined) {
        try {
          switch (stateObject.action) {
            case REDIRECTION_ACTIONS.ACTION_STUDY_REDIRECT:
              handleStudyLinkRedirect(stateObject);
              return;

            case REDIRECTION_ACTIONS.ACTION_ECONSENT:
              handleEConsentRedirection(stateObject);
              return;

            case REDIRECTION_ACTIONS.ACTION_JOIN_STUDY:
              if (stateObject.groupIdentifier) {
                handleJoinGroupAction(stateObject);
              } else if (stateObject.studyIdentifier) {
                handleJoinStudyAction(stateObject);
              }
              
              return;
          
            default:
              break;
          }
        } catch (error) {
          // nothing goes here
        }
      }

      if (keys?.requiresOnboarding) {
        router.push({
          name: routeConstants.ONBOARDING.name
        });
      } else if (stateObject?.next) {
        const next = typeof stateObject.next === 'string' ? stateObject.next : undefined;
        const isInternalPath = next && next.startsWith('/') && !next.startsWith('//');
        if (isInternalPath) {
          router.replace(next);
          return;
        }
      } else {
        router.push({
          name: routeConstants.STUDY_FETCH.name
        });
      }

      
    } else if (route.fullPath.startsWith('/investigation') && route.query.caseId) {
      authStore.checkAuth();
      
      const stateObject: OAuthState = {
        action: REDIRECTION_ACTIONS.ACTION_STUDY_REDIRECT,
        studyIdentifier: route.params.studyId as string,
        caseIdentifier: route.query.caseId as string,
        formIdentifier: route.query.formId as string,
        entryIdentifier: route.query.entryId as string,
        mode: route.query.mode as string,
        redirectUrl: route.query.redirectUrl as string,
        autoRedirect: route.query.autoRedirect as string,
        lang: route.query.lang as string
      };
      
      if (!authStore.userLoggedIn) {
        studypagesOauth(JSON.stringify(stateObject));
      } else {
        await handleStudyLinkRedirect(stateObject);
      }
    } else if (route.fullPath.startsWith(routeConstants.ECONSENT_VERIFICATION.path)) {
      const stateObject: OAuthState = {
        action: REDIRECTION_ACTIONS.ACTION_ECONSENT,
        studyIdentifier: route.query.studyId as string,
        caseIdentifier: route.query.caseId as string,
        formIdentifier: route.query.formId as string,
        entryIdentifier: route.query.entryId as string
      };
      
      if (!authStore.userLoggedIn) {
        studypagesOauth(JSON.stringify(stateObject));
      } else {
        await handleEConsentRedirection(stateObject);
      }
    }
  };

  const handleStudyLinkRedirect = async (stateObject: OAuthState) => {
    if (stateObject.studyIdentifier) {
      await studyStore.getStudy(stateObject.studyIdentifier);

      let url = `/study/${stateObject.studyIdentifier}/dash/data/case/${stateObject.caseIdentifier}`;

      if (stateObject.formIdentifier) {
        url += `/form/${stateObject.formIdentifier}`;
      } else if (stateObject.entryIdentifier) {
        const entry = await EntryService.getEntry(stateObject.entryIdentifier);
        if (entry) {
          url += `/form/${entry.formIdentifier}`;
        }
      }

      if (stateObject.entryIdentifier) {
        url += `/entry/${stateObject.entryIdentifier}`;
      } else {
        url += `/entry/new`;
      }

      if (stateObject.redirectUrl) {
        if (
          stateObject.redirectUrl.startsWith(STUDYPAGES_BASE_URL) ||
          stateObject.redirectUrl.startsWith(STUDYPAGES_BASE_URL) ||
          (ENVIRONMENT === 'local' && stateObject.redirectUrl.startsWith('http://localhost'))
        ) {
          url += `?redirectUrl=${stateObject.redirectUrl}`;
        }
      }

      if (stateObject.mode) {
        url += `&mode=${stateObject.mode}`;
      }

      if (stateObject.autoRedirect) {
        url += `&autoRedirect=${stateObject.autoRedirect}`;
      }

      if (stateObject.lang) {
        url += `&lang=${stateObject.lang}`;
      }

      router.push(url);
    }
  };

  const handleEConsentRedirection = async (stateObject: OAuthState) => {
    if (stateObject.studyIdentifier) {
      await studyStore.getStudy(stateObject.studyIdentifier);

      let url = `/study/${stateObject.studyIdentifier}/dash/data/case/${stateObject.caseIdentifier}`;

      if (stateObject.formIdentifier) {
        url += `/form/${stateObject.formIdentifier}`;
      }

      if (stateObject.entryIdentifier) {
        url += `/entry/${stateObject.entryIdentifier}`;
      }

      router.push(url);
    }
  };

  const handleJoinStudyAction = async (stateObject: OAuthState) => {
    const response = await StudyService.joinStudy(stateObject.studyIdentifier!, stateObject.token);
    if (response.investigationIdentifier) {
      // This means success
      studyStore.getStudy(response.investigationIdentifier);
      router.push({
        name: routeConstants.DASHBOARD.name,
        params: { studyIdentifier: response.investigationIdentifier }
      });
    } else {
      // show error
    }
  };

  const handleJoinGroupAction = async (stateObject: OAuthState) => {
    if (stateObject.token) {
      const response = await studyGroupService.joinGroup(stateObject.groupIdentifier!, stateObject.token);
      if (response.investigationIdentifier) {
        // This means success
        studyStore.getStudy(response.investigationIdentifier);
        router.push({
          name: routeConstants.DASHBOARD.name,
          params: { studyIdentifier: response.investigationIdentifier }
        });
      } else {
        // show error
      }
    } else {
      router.push({
        name: routeConstants.DASHBOARD.name
      });
    }
  };

  const oauthLogin = async (code: string) => {
    const keysData: PostKeys = {
      code,
      frontendFlag: ENVIRONMENT === `local` ? WEB_FRONTEND_LOCAL : WEB_FRONTEND,
      localHostUrl: ENVIRONMENT === `local` ? FRONTEND_BASE_URL : undefined
    };
    let keys = await LoginService.loginUser(keysData);
    if (keys.oauthAccessToken) {
      keysData.accessToken = keys.oauthAccessToken;
      delete keysData.code;
      keys = await LoginService.loginUser(keysData);
    }
    LocalStorageUtils.storeUserKeys(keys);
    await userStore.getUser(keys.identifier!);
    keys.avatar = userStore.user?.avatar;
    keys.name = userStore.user?.name;
    LocalStorageUtils.storeUserKeys(keys);
    LocalStorageUtils.onLoginSuccess();
    return keys;
  };

  const signUpData = ref<PostSignUp>({});

  const signUpLoader = ref(new LoadingStateModel({ isLoading: false }));
  const signUp = async () => {
    try {
      signUpLoader.value.startLoading();
      const data = await LoginService.signUp(signUpData.value);
      signUpLoader.value.setSuccess('New account created!');
      return data;
    } catch (error) {
      signUpLoader.value.setError(error);
      const axiosError = error as AxiosError<{ message?: string }>;
      if (axiosError.response?.data?.message) {
        signupError.value = `${axiosError.response?.data?.message}! Sign up failed! Please try again after some time.`;
      } else {
        signupError.value = `${(error as Error)?.message}! Sign up failed! Please try again after some time.`;
      }
    } finally {
      signUpLoader.value.stopLoadingAndFinish();
    }
  };

  const deepLinkData = ref<DeepLink>();

  const resolveBranchLink = async () => {
    // this will start the loading if the signup page is accessed via a deep link
    signUpLoader.value.startLoading();
    const token = route.query.invite ? route.query.invite as string : route.query.token as string;
    const studyId = route.query.investigationIdentifier as string;
    const groupId = route.query.groupIdentifier as string;
    try {
      const deepLink = await DeepLinkService.getDeepLink(token, studyId, groupId);
      deepLinkData.value = await DeepLinkService.getDeepLinkData(deepLink.link);
      signUpData.value.email = deepLinkData.value.email;
      if (
        deepLinkData.value.action !== DEEPLINK_ACTIONS.USER_JOIN_STUDY &&
        deepLinkData.value.action !== DEEPLINK_ACTIONS.NON_USER_JOIN_STUDY
      ) {
        signUpData.value.token = deepLinkData.value.token;
      }
      signUpData.value.investigationIdentifier = deepLinkData.value.studyId;
      signUpData.value.groupIdentifier = deepLinkData.value.groupId;
    } catch (error) {
      signUpLoader.value.setError(error);
      const axiosError = error as AxiosError<{ message?: string }>;
      if (axiosError.response?.data?.message) {
        linkError.value = `${axiosError.response.data.message}! Please ask your study administrator for a new invite link.`;
      }
    } finally {
      signUpLoader.value.stopLoadingAndFinish();
    }
  };

  const getDeepLinkData = async (link: string) => {
    deepLinkData.value = await DeepLinkService.getDeepLinkData(link);
    signUpData.value.email = deepLinkData.value.email;
    signUpData.value.token = deepLinkData.value.token;
    signUpData.value.investigationIdentifier = deepLinkData.value.studyId;
  };

  return {
    studypagesOauth,
    oauthLogin,
    handleOAuthRedirections,
    resolveBranchLink,
    signUp,
    getDeepLinkData,
    signUpData,
    signUpLoader,
    deepLinkData,
    linkError,
    signupError
  };
});
